/**
 * ShareDialog — modal for creating/copying/revoking a public share link for a run.
 * Replaces the old window.confirm flow with a proper shadcn dialog.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/api/auth-client";

interface ShareDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	runId: number;
	shareToken: string | null | undefined;
}

export function ShareDialog({ open, onOpenChange, runId, shareToken }: ShareDialogProps) {
	const queryClient = useQueryClient();
	const [loading, setLoading] = useState(false);
	const [shareUrl, setShareUrl] = useState<string | null>(null);

	const isShared = !!shareToken || !!shareUrl;

	const handleCreate = async () => {
		setLoading(true);
		const { data, error } = await authClient.POST("/api/v1/runs/{run_id}/share", {
			params: { path: { run_id: runId } },
		});
		setLoading(false);
		if (error || !data) {
			toast.error("Failed to create share link");
			return;
		}
		const url = (data as { share_url: string }).share_url;
		setShareUrl(url);
		await queryClient.invalidateQueries({ queryKey: ["run", runId] });
	};

	const handleStopSharing = async () => {
		setLoading(true);
		const { error } = await authClient.DELETE("/api/v1/runs/{run_id}/share", {
			params: { path: { run_id: runId } },
		});
		setLoading(false);
		if (error) {
			toast.error("Failed to revoke share link");
			return;
		}
		toast.success("Public link disabled");
		setShareUrl(null);
		await queryClient.invalidateQueries({ queryKey: ["run", runId] });
		onOpenChange(false);
	};

	// Build the display URL from the existing token or freshly created one
	const displayUrl =
		shareUrl ?? (shareToken ? `${window.location.origin}/share/${shareToken}` : null);

	// Reset local state when dialog closes
	const handleOpenChange = (next: boolean) => {
		if (!next) setShareUrl(null);
		onOpenChange(next);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Share conversation</DialogTitle>
					<DialogDescription>
						{isShared
							? "Anyone with this link can view this conversation."
							: "This will create a public link. Anyone with the link will be able to view this conversation."}
					</DialogDescription>
				</DialogHeader>

				{isShared && displayUrl ? (
					<>
						<div className="flex items-center gap-2">
							<Input readOnly value={displayUrl} className="font-mono text-xs" />
							<CopyButton content={displayUrl} variant="outline" size="sm" />
						</div>
						<DialogFooter>
							<Button variant="destructive" onClick={handleStopSharing} disabled={loading}>
								Stop sharing
							</Button>
						</DialogFooter>
					</>
				) : (
					<DialogFooter>
						<Button onClick={handleCreate} disabled={loading}>
							Create public link
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}
